import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff, CheckCircle as CheckIcon, Password as PasswordIcon, Refresh as RefreshIcon } from "@mui/icons-material";
import { fieldSx, goldButtonSx, fadeUp, stagger } from "./signinStyles";

export const BUSINESS_TYPES = [
  { value: "proprietorship", label: "Proprietorship" },
  { value: "partnership", label: "Partnership" },
  { value: "llp", label: "LLP" },
  { value: "private_limited", label: "Private Limited" },
  { value: "public_limited", label: "Public Limited" },
  { value: "other", label: "Other" },
];

export const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat", "Haryana",
  "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
  "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana",
  "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal", "Andaman and Nicobar Islands", "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu", "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];

export const rules = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  mobile: /^[6-9]\d{9}$/,
  pan: /^[A-Z]{5}[0-9]{4}[A-Z]$/,
  gstin: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/,
  pincode: /^[1-9]\d{5}$/,
};

// A soft text field that clears its own error as the visitor types. The hint is a placeholder, not a label.
export function Field({ name, label, value, onChange, error, icon, type = "text", select, children, helper, required = true, inputProps, endAdornment, alwaysFloat, ...rest }) {
  const [focused, setFocused] = useState(false);
  // a date box shows its placeholder until it is focused, then turns into the date picker
  const asDate = type === "date";
  const shownType = asDate && !focused && !value ? "text" : type;

  const selectProps = select
    ? {
        displayEmpty: true,
        renderValue: (v) => {
          if (!v) return <span className="placeholder-text">{label}</span>;
          const match = React.Children.toArray(children).find((c) => c?.props?.value === v);
          return match ? match.props.children : v;
        },
      }
    : undefined;

  return (
    <TextField
      fullWidth
      size="small"
      margin="dense"
      variant="outlined"
      name={name}
      placeholder={label}
      type={shownType}
      select={select}
      value={value}
      onChange={onChange}
      onFocus={asDate ? () => setFocused(true) : undefined}
      onBlur={asDate ? () => setFocused(false) : undefined}
      required={false}
      error={!!error}
      helperText={error || helper || " "}
      sx={fieldSx}
      inputProps={{ "aria-label": label, ...inputProps }}
      SelectProps={selectProps}
      InputProps={{
        startAdornment: icon ? <InputAdornment position="start">{icon}</InputAdornment> : undefined,
        endAdornment,
      }}
      {...rest}
    >
      {children}
    </TextField>
  );
}

export function PasswordField({ show, onToggle, ...props }) {
  return (
    <Field
      {...props}
      type={show ? "text" : "password"}
      endAdornment={
        <InputAdornment position="end">
          <IconButton onClick={onToggle} edge="end" tabIndex={-1} sx={{ color: "#8a6a24" }}>
            {show ? <Visibility /> : <VisibilityOff />}
          </IconButton>
        </InputAdornment>
      }
    />
  );
}

// A section heading used to group the business fields
export function SectionTitle({ icon, children, delay = 0 }) {
  return (
    <Box sx={{ ...stagger(delay), display: "flex", alignItems: "center", gap: 1, mt: 1.5, mb: 0.25 }}>
      <Box sx={{ width: 28, height: 28, borderRadius: "9px", bgcolor: "#fbf3df", color: "#8a6a24", display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 17 } }}>{icon}</Box>
      <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, letterSpacing: "0.8px", textTransform: "uppercase", color: "#5c4a1c" }}>{children}</Typography>
      <Box sx={{ flex: 1, height: "1px", bgcolor: "rgba(200,160,84,0.25)" }} />
    </Box>
  );
}

// Numbered steps with a line that fills as you move on
export function StepDots({ steps, current }) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1 }}>
      {steps.map((label, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <React.Fragment key={label}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: 56 }}>
              <Box
                sx={{
                  width: 26, height: 26, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "0.75rem", fontWeight: 700,
                  bgcolor: done || active ? "#C8A054" : "#f1ead6",
                  color: done || active ? "#fff" : "#9a8c63",
                  boxShadow: active ? "0 0 0 4px rgba(200,160,84,0.2)" : "none",
                  transition: "all 0.4s ease",
                }}
              >
                {done ? <CheckIcon sx={{ fontSize: 16 }} /> : i + 1}
              </Box>
              <Typography sx={{ mt: 0.4, fontSize: "0.68rem", fontWeight: active ? 700 : 500, color: active ? "#5c4a1c" : "#9a8c63", transition: "color 0.4s ease" }}>{label}</Typography>
            </Box>
            {i < steps.length - 1 && (
              <Box sx={{ flex: 1, height: 2, mt: "12px", borderRadius: 1, bgcolor: "#f1ead6", overflow: "hidden" }}>
                <Box sx={{ height: "100%", width: done ? "100%" : "0%", bgcolor: "#C8A054", transition: "width 0.5s ease" }} />
              </Box>
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
}

// One code box: shows where the code went, takes the 6 digits, resends after a countdown, turns green when confirmed.
export function OtpBox({ icon, title, target, verified, onVerify, onResend, error }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(30);
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    if (wait <= 0) return undefined;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  const verify = async () => {
    setLocalError("");
    if (!/^\d{6}$/.test(code)) {
      setLocalError("Enter the 6-digit code");
      return;
    }
    setBusy(true);
    try {
      await onVerify(code);
    } catch (e) {
      setLocalError(e.message || "That code did not work");
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setLocalError("");
    setBusy(true);
    try {
      await onResend();
      setWait(30);
      setCode("");
    } catch (e) {
      setLocalError(e.message || "Could not resend the code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        mb: 1.5,
        borderRadius: "16px",
        border: "1px solid",
        borderColor: verified ? "rgba(46,125,50,0.35)" : "rgba(200,160,84,0.28)",
        bgcolor: verified ? "#f3faf3" : "#fffdf8",
        transition: "background-color 0.5s ease, border-color 0.5s ease",
        animation: `${fadeUp} 0.5s both`,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        <Box sx={{ width: 34, height: 34, borderRadius: "11px", bgcolor: verified ? "#e1f2e1" : "#fbf3df", color: verified ? "#2e7d32" : "#8a6a24", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.5s ease" }}>
          {verified ? <CheckIcon sx={{ fontSize: 20 }} /> : icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#222" }}>{verified ? `${title} verified` : title}</Typography>
          <Typography sx={{ fontSize: "0.78rem", color: "text.secondary", overflowWrap: "anywhere" }}>{verified ? target : `Code sent to ${target}`}</Typography>
        </Box>
      </Box>

      {!verified && (
        <Box sx={{ mt: 1.25 }}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
            <TextField
              fullWidth
              size="small"
              placeholder="6-digit code"
              value={code}
              onChange={(e) => { setCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setLocalError(""); }}
              inputProps={{ inputMode: "numeric", maxLength: 6 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><PasswordIcon sx={{ color: "#C8A054", fontSize: 20 }} /></InputAdornment> }}
              error={!!(localError || error)}
              helperText={localError || error || " "}
              onKeyDown={(e) => { if (e.key === "Enter") verify(); }}
              sx={fieldSx}
            />
            <Button variant="contained" onClick={verify} disabled={busy} sx={{ ...goldButtonSx, py: 0.9, px: 2.25, minWidth: 92, mt: 0 }}>
              {busy ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Verify"}
            </Button>
          </Box>
          <Typography variant="body2" sx={{ fontSize: "0.78rem", color: "text.secondary" }}>
            {wait > 0 ? (
              `Resend code in ${wait}s`
            ) : (
              <Link component="button" type="button" underline="hover" onClick={resend} sx={{ color: "#8a6a24", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: "0.78rem" }}>
                <RefreshIcon sx={{ fontSize: 15 }} />
                Resend code
              </Link>
            )}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
